import React from 'react';
import { CustomStepProps } from '../../types/survey';
import { Button } from '../ui/Button';

/**
 * Lifestyle Category Introduction Screen
 * Introduces the lifestyle section of the questionnaire
 */
export const LifestyleCategoryIntro: React.FC<CustomStepProps> = ({
  onNext,
  currentStep,
  totalSteps
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-blue-lightest via-white to-royal-gold-lightest flex items-center justify-center p-4">
      <div className="max-w-3xl w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section with Gradient */}
          <div className="bg-gradient-to-r from-royal-blue to-royal-gold p-8 text-white">
            <div className="text-center">
              <div className="mb-4">
                <span className="text-6xl">🏃‍♂️</span>
              </div>
              <h1 className="text-4xl font-bold mb-4">
                Lifestyle Assessment
              </h1>
              <p className="text-xl opacity-90">
                Your Daily Habits Shape Your Longevity Journey
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {/* Introduction */}
            <div className="mb-8 text-center">
              <p className="text-lg text-gray-700 leading-relaxed">
                Let's explore your lifestyle habits. This questionnaire will help us understand your daily routines and behaviors 
                that directly impact your health and longevity, so we can give you tailored advice on how to improve them.
              </p>
            </div>

            {/* What We'll Cover */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
                What We'll Explore
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">💤</span>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Sleep Patterns</h3>
                      <p className="text-sm text-gray-600">
                        Quality and duration of your restorative sleep
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">🥗</span>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Nutrition Habits</h3>
                      <p className="text-sm text-gray-600">
                        Dietary choices and eating patterns
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">🏋️</span>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Physical Activity</h3>
                      <p className="text-sm text-gray-600">
                        Exercise frequency and movement throughout the day
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">🧘</span>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Stress Management</h3>
                      <p className="text-sm text-gray-600">
                        How you cope with daily stressors
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">🚬</span>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Habits & Substances</h3>
                      <p className="text-sm text-gray-600">
                        Smoking, alcohol, and other lifestyle factors
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">👥</span>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Social Connections</h3>
                      <p className="text-sm text-gray-600">
                        Relationships and community engagement
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Why This Matters */}
            <div className="bg-gradient-to-r from-royal-gold to-royal-blue bg-opacity-5 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                🎯 Why Lifestyle Matters for Longevity
              </h3>
              <div className="space-y-3 text-gray-700">
                <p className="flex items-start">
                  <span className="text-royal-gold mr-2">•</span>
                  <span>Up to 80% of chronic diseases can be prevented through lifestyle modifications</span>
                </p>
                <p className="flex items-start">
                  <span className="text-royal-gold mr-2">•</span>
                  <span>Daily habits have a cumulative effect on your healthspan and lifespan</span>
                </p>
                <p className="flex items-start">
                  <span className="text-royal-gold mr-2">•</span>
                  <span>Small, consistent changes can lead to significant health improvements</span>
                </p>
                <p className="flex items-start">
                  <span className="text-royal-gold mr-2">•</span>
                  <span>Your lifestyle choices influence gene expression and biological aging</span>
                </p>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Section Progress</span>
                <span className="text-sm font-semibold text-royal-gold">
                  Step {currentStep + 1} of {totalSteps}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-royal-gold to-royal-blue h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                />
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={onNext}
                variant="primary"
                size="lg"
                className="w-full sm:w-auto group"
              >
                <span>Begin Lifestyle Assessment</span>
                <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
              </Button>
            </div>

            {/* Time Estimate */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                ⏱️ This questionnaire takes approximately 3-5 minutes
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};